import { flatten } from "flat";

const DEFAULT_LIMITS = {
  maxArrayLength: 100,     // Max number of array items to process
  maxDepth: 10,           // Max nesting depth
  maxEntries: 500,        // Max total number of flattened entries
  maxStringLength: 100,   // Max length for string values
  maxObjectPreview: 50    // Max length for object/array preview strings
};

// Using a unique delimiter to avoid conflicts with JSON keys that may contain dots
const FLATTEN_DELIMITER = "||~||";

export function flattenObject(obj, customLimits = {}) {

  const limits = { ...DEFAULT_LIMITS, ...customLimits };
  const paths = [];
  let entryCount = 0;

  // Add root object
  paths.push({
    path: "$",
    value: obj,
    type: typeof obj,
  });

  const processArray = (arr, basePath, depth) => {
    const truncated = _truncateValue(arr, limits);
    paths.push({
      path: `${basePath}[*]`,
      value: truncated.preview,
      type: 'array',
      meta: {
        length: arr.length,
        truncated: arr.length > limits.maxArrayLength
      }
    });

    const limitedArr = arr.slice(0, limits.maxArrayLength);
    limitedArr.forEach((item, index) => {
      if (entryCount >= limits.maxEntries) return;

      const itemPath = `${basePath}[${index}]`;
      const truncated = _truncateValue(item, limits);
      paths.push({
        path: itemPath,
        value: truncated.preview,
        type: truncated.type,
        meta: truncated.truncated ? {
          truncated: true,
          fullLength: truncated.fullLength
        } : undefined
      });
      entryCount++;

      if (typeof item === "object" && item !== null) {
        processObject(item, itemPath, depth + 1);
      }
    });
  };

  const processObject = (obj, basePath = "$", depth = 0) => {
    if (depth > limits.maxDepth) {
      paths.push({
        path: basePath,
        value: `[Object]`,
        type: 'object',
        meta: {
          maxDepthExceeded: true,
          depth
        }
      });
      return;
    }

    const flatObj = flatten(obj, {
      safe: true,
      maxDepth: limits.maxDepth,
      delimiter: FLATTEN_DELIMITER,
    });

    for (const [key, value] of Object.entries(flatObj)) {
      if (entryCount >= limits.maxEntries) {
        return;
      }

      // Build the JSONata suffix (".prop[0].sub") and prepend basePath ("$")
      const suffix = _buildJsonataSuffix(key, FLATTEN_DELIMITER);
      const path   = basePath + suffix;

      if (Array.isArray(value)) {
        processArray(value, path, depth + 1);
      } else {
        const truncated = _truncateValue(value, limits);
        paths.push({
          path,
          value: truncated.preview,
          type: truncated.type,
          meta: truncated.truncated ? {
            truncated: true,
            fullLength: truncated.fullLength
          } : undefined
        });
        entryCount++;
      }
    }
  };

  processObject(obj);
  return paths;
}

function _buildJsonataSuffix(flatKey, delimiter = FLATTEN_DELIMITER) {
  return flatKey
    .split(delimiter)
    .map(segment => {

      // For digit-only segments, wrap into [index]
      if (/^\d+$/.test(segment)){
        return `[${segment}]`;
      }

      // Matches valid JS identifiers (no spaces or punctuation, and doesn’t start with a digit)
      // Should be safe to use with dot-notation. e.g. `.propertyName`
      if (/^[a-zA-Z_]\w*$/.test(segment)) {
        return `.${segment}`;
      }

      // For special characters, use dot notation with quotes
      // This is to avoid issues where the key contains a dot or is not a valid JS identifier.
      //
      // E.g. response from postman-echo
      // {
      //    files: {
      //    "testFile.txt": "data:application/octet-stream;base64,VGhpcyBpcy..."
      // }
      //
      // "$.files.testFile.txt" will fail in jsonata when trying to access the value because it will be interpreted as a nested object.
      // $.files --> .testFile --> .txt
      // To avoid this, we need to use the safe key format to convert the key to a valid jsonata expression
      // E.g. `$.files."testFile.txt"` instead of $.files.testFile.txt
      const safe = segment.replace(/["\\]/g, '\\$&');
      return `."${safe}"`;
    })
    .join('');
}

function _truncateValue(value, limits) {

  const fileInfo = _isLikelyFile(value);
  if (fileInfo) {
    return {
      preview: '[File]',
      type: 'file',
      truncated: true,
      meta: {
        encoding: fileInfo.type,
        mimeType: fileInfo.mimeType,
        size:
          fileInfo.type === 'base64' ? value.length :
            value.byteLength || value.length || undefined
      }
    };
  }

  if (typeof value === 'string' && value.length > limits.maxStringLength) {
    return {
      preview: `${value.substring(0, limits.maxStringLength)}...`,
      type: 'string',
      fullLength: value.length,
      truncated: true
    };
  }

  if (Array.isArray(value)) {
    return {
      preview: `Array(${value.length})`,
      type: 'array',
      fullLength: value.length,
      truncated: value.length > limits.maxArrayLength
    };
  }

  if (typeof value === 'object' && value !== null) {
    const preview = JSON.stringify(value).substring(0, limits.maxObjectPreview);
    return {
      preview: `${preview}...`,
      type: 'object',
      truncated: true
    };
  }

  return {
    preview: value,
    type: typeof value,
    truncated: false
  };
}


function _isLikelyFile(value) {
  // base64-encoded Data URL
  if (
    typeof value === 'string' &&
    value.length > 50 &&
    value.startsWith('data:') &&
    /;base64,/.test(value)
  ) {
    return {
      type: 'base64',
      mimeType: value.split(';')[0]?.split(':')[1] || 'application/octet-stream'
    };
  }

  // ArrayBuffer / Uint8Array
  if (
    (typeof ArrayBuffer !== 'undefined' && value instanceof ArrayBuffer) ||
    (typeof Uint8Array !== 'undefined' && value instanceof Uint8Array)
  ) {
    return {
      type: 'arraybuffer',
      mimeType: 'application/octet-stream'
    };
  }

  return false;
}
